import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import FormTemplates from './FormTemplates';
import FormsList from './CompletedForms/FormsList';
import AssignedForms from './AssignedForms/AssignedForms';
import ApproveForms from './ApproveForms/ApproveForms';
import ManualForms from './ManualForms/ManualForms';
import FormImport from './FormImport/FormImport';
import FormsHome from './FormsHome/FormsHome';
import FormsSubpageLayout from './FormsHome/FormsSubpageLayout';
import FormsDraftsPage from './FormsHome/FormsDraftsPage';

import BoardCardDrawerStack from '../boards/BoardCardDrawerStack';
import useBoardCardFromColumn from '../boards/hooks/useBoardCardFromColumn';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import { ReactComponent as WorkflowIcon } from '../assets/icons/workflows.svg';

import { getNumberOfAssignedAndDraftForms, getFormApprovalCount } from './formHelpers';

import BreadCrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';

import Permissions from '../auth/Permissions';

import {
  getTemplates,
  getStatuses,
  getCustomTables,
  getDrafts,
  getAssignedForms,
  getUserFormFilterViews,
} from './state/forms.actions';
import {
  getEmails,
} from '../emails/state/email.actions';
import { getBillingRates } from '../billingRates/state/billingRates.actions';
import { getCustomerCustomFieldTemplate, getCustomerLabels, getCustomers } from '../contacts/customers/state/customers.actions';
import { getUserLabels, getUserTeams } from '../users/state/users.actions';
import { getSchedule } from '../schedule/state/schedule.actions';
import { getEquipment, getEquipmentTypes } from '../equipment/state/equipment.actions';
import { getCompanyImageURL } from '../settings/state/settings.actions';
import { getVendorLabels } from '../contacts/vendors/state/vendors.actions';
import { getCustomFields } from '../timecards/state/timecards.actions';
import { getLabels } from '../labels/state/labels.actions';
import { getProjectEquipment, getProjectCustomFieldTemplate } from '../projects/state/projects.actions';
import {
  getBoards,
  getBoardCardTemplates,
  getBoardStatuses,
} from '../boards/state/boards.actions';
import { isFilterActive } from '../schedule/GanttSchedule/ganttScheduleHelpers';
import { getEclipseDivisions } from '../settings/Integrations/Eclipse/state/eclipse.actions';
import { findPayrollStartAndEndDates, getFirstPayrollDay } from '../helpers/payroll';
import {
  getDraftCount,
  getSubmittedFormsCount,
  countApprovalsThisWeek,
  countDraftsThisWeek,
  FORMS_SUBPAGE_KEYS,
  getFormsSubpageKey,
  getFormsSubpageMeta,
  trackFormsSubpageView,
  countAssignedFormsThisWeek,
} from './FormsHome/formsHomeHelpers';

const crumbs = [{ text: 'Forms', icon: 'form' }];

export default function Forms({
  history,
  location: {
    pathname = '',
  } = {},
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const allForms = useSelector((state) => state.forms);
  const formApprovals = useSelector((state) => state.forms.approvalSummary);
  const forms = useSelector((state) => state.forms.forms);
  const formFilters = useSelector((state) => state.forms.formFilters);
  const drafts = useSelector((state) => state.forms.drafts);
  const { connectedToEclipse = false } = useSelector((state) => state.settings.company);

  const [range, setRange] = useState(null);
  const [statePayPeriodRange, setStatePayPeriodRange] = useState(null);
  const formList = useMemo(() => Object.values(forms), [forms]);

  const {
    selectedBoardId,
    ourBoard,
    cardType,
    userBoardPermissions,
    onCardColumnClick,
    onBoardDrawerClose,
  } = useBoardCardFromColumn();

  const {
    settings: {
      payPeriod,
      payPeriodDates,
      semiMonthlyPayPeriodDates,
      uxRedesignPt1: hasRedesign,
    } = {},
  } = useSelector((state) => state.settings.company ?? {});
  useEffect(() => {
    if (payPeriod && payPeriodDates) {
      const firstPayrollDay = getFirstPayrollDay({
        payPeriod,
        payPeriodDates,
        semiMonthlyPayPeriodDates,
      });
      const payPeriodRange = findPayrollStartAndEndDates({
        payPeriodFirstDay: firstPayrollDay,
        payPeriod,
        semiMonthlyPayPeriodDates,
      });
      setStatePayPeriodRange(payPeriodRange.map((date) => date.toMillis()));
      setRange(payPeriodRange.map((date) => date.toMillis()));
    }
  }, [payPeriod, payPeriodDates, semiMonthlyPayPeriodDates]);

  const formApprovalCount = useMemo(() => (
    getFormApprovalCount(formApprovals)
  ), [formApprovals]);

  const formAssignedCount = useMemo(() => (
    getNumberOfAssignedAndDraftForms(allForms)
  ), [allForms]);

  const draftCount = useMemo(() => getDraftCount(drafts), [drafts]);

  const submittedWeekRange = useMemo(() => ([
    DateTime.local().startOf('week').toMillis(),
    DateTime.local().endOf('week').toMillis(),
  ]), []);

  const homeCounts = useMemo(() => ({
    approve: formApprovalCount,
    assigned: formAssignedCount,
    drafts: draftCount,
    submitted: getSubmittedFormsCount(forms, submittedWeekRange),
    log: getSubmittedFormsCount(forms, statePayPeriodRange),
  }), [
    formApprovalCount,
    formAssignedCount,
    draftCount,
    forms,
    submittedWeekRange,
    statePayPeriodRange,
  ]);

  const homeTrends = useMemo(() => {
    const approveThisWeek = countApprovalsThisWeek(formApprovals);
    const draftsThisWeek = countDraftsThisWeek(drafts);
    const assignedFormsThisWeek = countAssignedFormsThisWeek(allForms);
    return {
      approve: approveThisWeek > 0 ? `+${approveThisWeek} this week` : '+0 this week',
      assigned: assignedFormsThisWeek > 0 ? `+${assignedFormsThisWeek} this week` : '+0 this week',
      drafts: `+${draftsThisWeek} this week`,
      submitted: null,
      log: 'this pay period',
    };
  }, [formApprovals, drafts, allForms]);

  const onRangeChange = useCallback((newRange) => {
    if (newRange && newRange.length === 2) {
      setRange(newRange);
    }
  }, []);

  const hasEditForms = Permissions.has('FORMS_WRITE');

  useEffect(() => {
    dispatch(getTemplates({ getPreview: !hasRedesign }));
  }, [hasRedesign]);

  useEffect(() => {
    dispatch(getEmails());
    dispatch(getBillingRates());
    dispatch(getStatuses());
    dispatch(getCustomers());
    dispatch(getCustomerLabels());
    dispatch(getCustomTables());
    dispatch(getUserLabels());
    dispatch(getUserTeams());
    dispatch(getDrafts());
    dispatch(getAssignedForms());
    dispatch(getSchedule());
    dispatch(getEquipment());
    dispatch(getProjectEquipment());
    dispatch(getCompanyImageURL());
    dispatch(getVendorLabels());
    dispatch(getCustomFields());
    dispatch(getLabels());
    dispatch(getBoards());
    dispatch(getBoardCardTemplates());
    dispatch(getUserFormFilterViews());
    dispatch(getProjectCustomFieldTemplate());
    dispatch(getEquipmentTypes());
    dispatch(getCustomerCustomFieldTemplate());
    dispatch(getBoardStatuses());
  }, []);

  useEffect(() => {
    if (connectedToEclipse) dispatch(getEclipseDivisions());
  }, [connectedToEclipse]);

  const subpageKey = getFormsSubpageKey(pathname);
  const [formHeaderAction, setFormHeaderAction] = useState(null);

  useEffect(() => {
    if (
      subpageKey !== FORMS_SUBPAGE_KEYS.APPROVE
      && subpageKey !== FORMS_SUBPAGE_KEYS.LOG
      && subpageKey !== FORMS_SUBPAGE_KEYS.SUBMITTED
    ) {
      setFormHeaderAction(null);
    }

    trackFormsSubpageView(subpageKey);
  }, [subpageKey]);

  const subpageMeta = useMemo(() => getFormsSubpageMeta({
    approvalCount: homeCounts.approve,
    assignedCount: homeCounts.assigned,
    draftCount: homeCounts.drafts,
    submittedCount: homeCounts.submitted,
    t,
  }), [homeCounts, t]);

  const query = useMemo(() => {
    if (formFilters) {
      const { dateRange } = formFilters;
      const queryObj = { ...formFilters };
      const startTime = dateRange?.[0]?.valueOf() ?? range[0];
      const endTime = dateRange?.[1]?.valueOf() ?? range[1];
      queryObj.startTime = startTime;
      queryObj.endTime = endTime;
      if (!queryObj.formNumber) delete queryObj.formNumber;
      delete queryObj.dateRange;
      delete queryObj.filterView;
      delete queryObj.filterViewName;
      return queryObj;
    }

    return {};
  }, [formFilters, range]);

  const isFiltersActive = useMemo(() => {
    const formFiltersObj = formFilters ?? {};
    return isFilterActive(formFiltersObj);
  }, [formFilters]);

  const renderSubpageContent = () => {
    switch (subpageKey) {
      case FORMS_SUBPAGE_KEYS.APPROVE:
        return (
          <ApproveForms
            onCardColumnClick={onCardColumnClick}
            registerFormHeaderAction={setFormHeaderAction}
          />
        );
      case FORMS_SUBPAGE_KEYS.ASSIGNED:
        return <AssignedForms history={history} onCardColumnClick={onCardColumnClick} />;
      case FORMS_SUBPAGE_KEYS.DRAFTS:
        return <FormsDraftsPage />;
      case FORMS_SUBPAGE_KEYS.SUBMITTED:
        return (
          <FormsList
            topOffset={400}
            forms={formList}
            range={submittedWeekRange}
            onRangeChange={onRangeChange}
            query={query}
            isFilterActive={isFiltersActive}
            enableFormFilters
            onCardColumnClick={onCardColumnClick}
            hideExportButton
            hideDate
            shouldApplyRange
            registerFormHeaderAction={setFormHeaderAction}
          />
        );
      case FORMS_SUBPAGE_KEYS.LOG:
        if (!Permissions.has('FORMS_READ_LOGS')) {
          history.replace('/forms');
          return null;
        }
        return (
          <FormsList
            topOffset={400}
            forms={formList}
            range={range}
            onRangeChange={onRangeChange}
            query={query}
            hideDate={!!formFilters?.dateRange}
            isFilterActive={isFiltersActive}
            enableFormFilters
            onCardColumnClick={onCardColumnClick}
            hideExportButton
            registerFormHeaderAction={setFormHeaderAction}
          />
        );
      case FORMS_SUBPAGE_KEYS.MANUAL:
        return <ManualForms visible history={history} />;
      case FORMS_SUBPAGE_KEYS.TEMPLATES:
        if (!Permissions.has('FORMS_READ_TEMPLATES')) {
          history.replace('/forms');
          return null;
        }
        return <FormTemplates history={history} activeTab="templates" />;
      case FORMS_SUBPAGE_KEYS.IMPORT:
        if (!hasEditForms) {
          history.replace('/forms');
          return null;
        }
        return <FormImport history={history} />;
      default:
        history.replace('/forms');
        return null;
    }
  };

  const headerActions = useMemo(() => {
    switch (subpageKey) {
      case FORMS_SUBPAGE_KEYS.MANUAL:
        if (Permissions.has('FORMS_READ_TEMPLATES')) {
          return (
            <OnTraccrButton
              title="Workflows"
              type="secondary"
              icon={<WorkflowIcon className="forms-subpage-action-button-icon" />}
              iconLeft
              onClick={() => history.push('/forms/templates')}
              className="forms-subpage-action-button"
            />
          );
        }
        return null;
      case FORMS_SUBPAGE_KEYS.APPROVE:
      case FORMS_SUBPAGE_KEYS.LOG:
      case FORMS_SUBPAGE_KEYS.SUBMITTED:
        return formHeaderAction;
      default:
        return null;
    }
  }, [subpageKey, history, formHeaderAction]);

  if (
    (!subpageKey && pathname !== '/forms')
    || !Permissions.has('FORMS_READ')
  ) {
    history.replace('/forms');
    return '';
  }

  const { title, subtitle } = subpageMeta[subpageKey] ?? {};

  return (
    <BreadCrumbContainer crumbs={crumbs} crumbStyle={{ display: 'none' }}>
      <div className="forms-home-container" style={{ top: hasRedesign ? 80 : 90 }}>
        {pathname === '/forms' && (
          <FormsHome
            history={history}
            onCardColumnClick={onCardColumnClick}
            range={statePayPeriodRange}
            counts={homeCounts}
            trends={homeTrends}
          />
        )}
        {pathname !== '/forms' && (
          <FormsSubpageLayout
            history={history}
            title={title}
            subtitle={subtitle}
            headerActions={headerActions}
          >
            {renderSubpageContent()}
          </FormsSubpageLayout>
        )}
      </div>
      <BoardCardDrawerStack
        boardId={selectedBoardId}
        ourBoard={ourBoard}
        cardType={cardType}
        userBoardPermissions={userBoardPermissions}
        onClose={onBoardDrawerClose}
      />
    </BreadCrumbContainer>
  );
}

Forms.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

Forms.defaultProps = {
  location: {},
};
